/**
 * /policies/leave/carry-forward-rules — Session 3c PR 4.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { CarryForwardRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function LeaveCarryForwardRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-leave-carry-forward-rules", le],
    queryFn: () => session3cApi.listCarryForwardRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<CarryForwardRuleItem>[] = [
    {
      headerKey: "policies.leave_carry_forward.col.leave_type",
      label: "Leave type",
      render: (r) => <span className="font-medium">{r.leave_type_name_en}</span>,
    },
    {
      headerKey: "policies.leave_carry_forward.col.cap",
      label: "Cap (days)",
      render: (r) => <span className="font-mono">{r.carry_forward_cap_days.value}</span>,
      traceableValue: (r) => ({ label: t("policies.leave_carry_forward.col.cap", "Cap"), stack: r.carry_forward_cap_days }),
    },
    {
      headerKey: "policies.leave_carry_forward.col.expiry",
      label: "Expiry (months)",
      render: (r) => <span className="font-mono text-xs">{r.expiry_months ?? "—"}</span>,
    },
    {
      headerKey: "policies.leave_carry_forward.col.negative",
      label: "Negative allowed",
      render: (r) => (
        <span className={`text-xs ${r.negative_balance_allowed ? "text-emerald-700" : "text-text-tertiary"}`}>
          {r.negative_balance_allowed ? "yes" : "no"}
        </span>
      ),
    },
  ];

  return (
    <PolicyListPage<CarryForwardRuleItem>
      title={t("policies.leave_carry_forward.title", "Carry-Forward Rules")}
      subtitle={t("policies.leave_carry_forward.subtitle", "Per-leave-type rollover cap, expiry policy, negative-balance allowed.")}
      agent="mutabaa"
      domain="leave_carry_forward"
      newButtonLabel={t("policies.leave_carry_forward.cta.new", "New Carry-Forward Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.leave_carry_forward.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.leave_carry_forward.suggestion.clean_body", "Carry-forward caps + expiry months + negative-balance toggles. Article 109 protected balances never expire."),
          primaryActionLabel: t("policies.leave_carry_forward.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "leave-carry-forward-rules-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.leave_carry_forward.title", "Carry-Forward Rules"),
        window_label: t("policies.leave_carry_forward.window", "across active leave types"),
      }}
    />
  );
}
